import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import FormSwitch from 'src/components/form-switch';
import {
  GlobalSettingsType,
  RemoteLoggingSettingsType,
} from 'src/context/global/types';

export type GlobalSettingsValidationError = {
  ignitionEnabled?: string;
  ignitionTimeout?: string;
  remoteLoggingDestination?: string;
  remoteLoggingPort?: string;
  remoteLoggingInterval?: string;
  ntpServer?: string;
  ntpQueryInterval?: string;
};

type SectionProps = {
  values: GlobalSettingsType;
  error?: GlobalSettingsValidationError;
  onChange: (next: GlobalSettingsType) => void;
  dataTestIdPrefix?: string;
  showDescription?: boolean;
};

export function GlobalSettingsIgnitionSection({
  values,
  error,
  onChange,
  dataTestIdPrefix,
  showDescription = true,
}: SectionProps) {
  const handleIgnitionChange = (value: boolean) => {
    onChange({ ...values, ignitionEnabled: value });
  };

  const handleIgnitionTimeoutChange = (value: string) => {
    onChange({ ...values, ignitionTimeout: parseInt(value, 10) || 0 });
  };

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900">
        Ignition Settings
      </h2>
      {showDescription && (
        <p className="mt-1 text-sm text-gray-600">
          Configure the ignition settings for the network.
        </p>
      )}
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <FormSwitch
            id="ignition-enabled"
            name="ignitionEnabled"
            label="Ignition Enabled"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-ignition-enabled`
                : undefined
            }
            checked={!!values.ignitionEnabled}
            onChange={handleIgnitionChange}
          />
          {error?.ignitionEnabled && (
            <p
              className="text-sm text-red-500"
              data-testid={
                dataTestIdPrefix
                  ? `${dataTestIdPrefix}-ignition-enabled-error`
                  : undefined
              }
            >
              {error.ignitionEnabled}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FormInput
            id="ignition-timeout"
            name="ignitionTimeout"
            label="Ignition Timeout (60-86400)"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-ignition-timeout`
                : undefined
            }
            value={values.ignitionTimeout?.toString() || ''}
            onChange={(e) => handleIgnitionTimeoutChange(e.target.value)}
            type="number"
            error={error?.ignitionTimeout}
          />
        </div>
      </div>
    </div>
  );
}

export function GlobalSettingsRemoteLoggingSection({
  values,
  error,
  onChange,
  dataTestIdPrefix,
  showDescription = true,
}: SectionProps) {
  const handleRemoteLoggingChange = (
    key: keyof RemoteLoggingSettingsType,
    value: any,
  ) => {
    onChange({
      ...values,
      remoteLogging: {
        ...values.remoteLogging,
        [key]: value,
      },
    });
  };

  return (
    <div className="flex flex-col gap-2 py-2">
      <h2 className="text-base font-semibold text-gray-900">
        Remote Logging Settings
      </h2>
      {showDescription && (
        <p className="mt-1 text-sm text-gray-600">
          Configure remote syslog server settings for network logging.
        </p>
      )}
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <FormSwitch
            id="remote-logging-enabled"
            name="remoteLoggingEnabled"
            label="Enabled"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-remote-logging-enabled`
                : undefined
            }
            checked={values.remoteLogging?.enabled || false}
            onChange={(_value) => handleRemoteLoggingChange('enabled', _value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FormInput
            id="remote-logging-destination"
            name="remoteLoggingDestination"
            label="Destination"
            placeholder="hostname or IP address"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-remote-logging-destination`
                : undefined
            }
            value={values.remoteLogging?.destination || ''}
            onChange={(e) =>
              handleRemoteLoggingChange('destination', e.target.value)
            }
            error={error?.remoteLoggingDestination}
            disabled={!values.remoteLogging?.enabled}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FormSelect
            id="remote-logging-protocol"
            name="remoteLoggingProtocol"
            label="Protocol"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-remote-logging-protocol`
                : undefined
            }
            value={values.remoteLogging?.protocol || 'udp'}
            onChange={(e) =>
              handleRemoteLoggingChange(
                'protocol',
                e.target.value as 'udp' | 'tcp',
              )
            }
            disabled={!values.remoteLogging?.enabled}
            options={[
              { label: 'UDP', value: 'udp' },
              { label: 'TCP', value: 'tcp' },
            ]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FormInput
            id="remote-logging-port"
            name="remoteLoggingPort"
            label="Port (1-65535)"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-remote-logging-port`
                : undefined
            }
            value={values.remoteLogging?.port?.toString() || ''}
            onChange={(e) =>
              handleRemoteLoggingChange(
                'port',
                parseInt(e.target.value, 10) || 514,
              )
            }
            type="number"
            min="1"
            max="65535"
            error={error?.remoteLoggingPort}
            disabled={!values.remoteLogging?.enabled}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FormSwitch
            id="remote-logging-mark"
            name="remoteLoggingMark"
            label="Periodic heartbeat messages"
            data-testid={
              dataTestIdPrefix
                ? `${dataTestIdPrefix}-remote-logging-mark`
                : undefined
            }
            checked={values.remoteLogging?.mark || false}
            onChange={(_value) => handleRemoteLoggingChange('mark', _value)}
            disabled={!values.remoteLogging?.enabled}
          />
        </div>

        {values.remoteLogging?.mark && (
          <div className="flex flex-col gap-2">
            <FormInput
              id="remote-logging-interval"
              name="remoteLoggingInterval"
              label="Interval of heartbeat messages (10-3600 seconds)"
              data-testid={
                dataTestIdPrefix
                  ? `${dataTestIdPrefix}-remote-logging-interval`
                  : undefined
              }
              value={values.remoteLogging?.interval?.toString() || ''}
              onChange={(e) =>
                handleRemoteLoggingChange(
                  'interval',
                  parseInt(e.target.value, 10) || 60,
                )
              }
              type="number"
              min="10"
              max="3600"
              error={error?.remoteLoggingInterval}
              disabled={!values.remoteLogging?.enabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}
